import { dehydrate, hydrate, QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { createRoot } from "react-dom/client";
import { Toaster } from "sonner";
import App from "./app/App";
import { router } from "./app/routes";
import { languageReady } from "./i18n";
import "./styles/index.css";
import { initDeepLinks } from "./native/deepLinks";
import { initStatusBar } from "./native/statusBar";
import { useAuthStore } from "./store/useAuthStore";
import { analyticsTracker } from "./shared/tracking/analyticsTracker";
import { getMyUserId } from "./chat/hooks/useMyUserId";
import { installStaleBuildReload } from "./helpers/staleBuildReload";
import { installSpaLinks } from "./app/spaLinks";

// Старая вкладка после деплоя: чанк не найден → одна тихая перезагрузка.
installStaleBuildReload();

// Трекер: просмотры самого владельца backend исключает по userId.
analyticsTracker.setUserIdGetter(getMyUserId);

const root = document.getElementById("root");

if (!root) {
  throw new Error("Root element not found");
}

const queryClient = new QueryClient();

/*
 * Prerendered TURAQ Pro pages (scripts/prerender.mjs) ship the query cache
 * they were rendered with and keep their static markup on screen until the
 * live page signals «pro:ready» — no splash, no skeleton, no refetch wait.
 */
type ProWindow = Window & { __PRO_STATE__?: unknown; __proDehydrate?: () => unknown };
const proWindow = window as ProWindow;
if (proWindow.__PRO_STATE__) {
  try {
    hydrate(queryClient, proWindow.__PRO_STATE__);
  } catch {
    /* a stale or malformed snapshot — the page simply fetches */
  }
}
proWindow.__proDehydrate = () =>
  dehydrate(queryClient, { shouldDehydrateQuery: (q) => q.state.status === "success" && q.queryKey[0] === "pro" });

let mountNode: HTMLElement = root;
if (document.documentElement.dataset.prerendered === "pro" && root.firstElementChild) {
  root.id = "root-ssr";
  mountNode = document.createElement("div");
  mountNode.id = "root";
  mountNode.style.display = "none";
  root.after(mountNode);
  const reveal = () => {
    if (!root.isConnected) return;
    mountNode.style.display = "";
    root.remove();
  };
  // Swap only after the static copy has fully painted (its images loaded), so
  // the first paint and LCP belong to the HTML, not to the app's JavaScript.
  const loaded = new Promise<void>((resolve) => (document.readyState === "complete" ? resolve() : window.addEventListener("load", () => resolve(), { once: true })));
  const ready = new Promise<void>((resolve) => window.addEventListener("pro:ready", () => resolve(), { once: true }));
  Promise.all([loaded, ready]).then(() => requestAnimationFrame(() => setTimeout(reveal, 0)));
  setTimeout(reveal, 8000);
}
useAuthStore.getState().updateTokens();
initDeepLinks(router);
installSpaLinks(router);
initStatusBar();

/** index.html's #boot-splash hides the crawler markup in #root until React has painted. */
function hideBootSplash() {
  const splash = document.getElementById("boot-splash");
  if (!splash) return;
  requestAnimationFrame(() =>
    requestAnimationFrame(() => {
      splash.classList.add("boot-out");
      setTimeout(() => splash.remove(), 300);
    }),
  );
}

const render = () => {
  // The SEO edge function (netlify/edge-functions/seo-html.ts) ships JSON-LD
  // for crawlers; the app sets its own via useDocumentMeta, so drop the copy.
  document.querySelectorAll("script[data-seo-edge]").forEach((el) => el.remove());
  createRoot(mountNode).render(
    <QueryClientProvider client={queryClient}>
      <App />
      <Toaster
        position="bottom-right"
        expand={true}
        richColors
        closeButton
        offset={{ bottom: 24, right: 16 }}
        mobileOffset={{
          bottom: "calc(var(--bottom-nav-height) + 12px)",
          right: 16,
          left: 16,
        }}
      />
    </QueryClientProvider>,
  );
  hideBootSplash();
};

// Wait for the active locale bundle so the first paint is in the right
// language; never block the app if that fetch fails.
languageReady.then(render, render);

export { queryClient };
